import type { FC, ReactNode } from 'react'

import Image from 'next/image'

import { Reveal } from '@/components/generals/reveal'
import { Marquee } from '@/components/generals/marquee'
import { SectionHeading } from '@/components/generals/section-heading'
import { results } from '@/constants/content'

export const Results: FC = (): ReactNode => {
    return (
        <section
            id='results'
            className='relative w-full overflow-hidden py-[120px] lg:py-[160px]'
        >
            <div className='mx-auto flex w-full max-w-[1200px] flex-col items-center px-6 md:px-8 lg:px-16'>
                <SectionHeading
                    eyebrow='Results'
                    segments={[
                        { text: 'Results speak for' },
                        { text: 'themselves.', serif: true }
                    ]}
                    subtitle='We’ve worked across a number of industries and have achieved some incredible results with some incredible people and brands.'
                />
            </div>

            <Reveal
                y={28}
                blur={10}
                delay={0.1}
                className='mt-14 w-full lg:mt-20'
            >
                <Marquee
                    duration={96}
                    gap='24px'
                >
                    {[...results, ...results].map((result, index) => (
                        <article
                            key={`${result.name}-${index}`}
                            className='group relative h-[420px] w-[300px] overflow-hidden rounded-xl bg-surface-1 ring-1 ring-white/8 sm:h-[460px] sm:w-[360px]'
                        >
                            <Image
                                src={result.image}
                                alt={`${result.name} campaign imagery`}
                                fill
                                sizes='(max-width: 640px) 300px, 360px'
                                className='object-cover transition-transform duration-[1200ms] ease-[cubic-bezier(0.44,0,0.56,1)] group-hover:scale-105'
                            />

                            <div
                                aria-hidden='true'
                                className='absolute inset-0 bg-gradient-to-t from-black via-black/55 to-transparent'
                            />

                            <div className='absolute inset-x-0 bottom-0 flex flex-col gap-4 p-5'>
                                <div className='flex flex-col gap-1.5'>
                                    <p className='text-sm leading-none tracking-[-0.02em] text-white/56'>
                                        {result.name}
                                    </p>

                                    <h3 className='text-[clamp(1.25rem,1.8vw,1.75rem)] leading-[1.15] font-medium tracking-[-0.04em] text-white'>
                                        {result.headline}
                                    </h3>
                                </div>

                                <ul className='flex flex-wrap gap-2'>
                                    {result.badges.map((badge) => (
                                        <li
                                            key={badge}
                                            className='rounded-lg bg-white/8 px-2.5 py-1.5 text-xs leading-none font-medium tracking-[-0.01em] text-white/80 ring-1 ring-white/10 backdrop-blur-[5px]'
                                        >
                                            {badge}
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        </article>
                    ))}
                </Marquee>
            </Reveal>
        </section>
    )
}
